/**
 * Drawer корзины складских операций.
 *
 * Показывает текущие позиции, позволяет менять количество, сохранить как
 * приход или расход (через POST /api/warehouse/docs).
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { Icon } from "@/components/prototype/Icon";
import { useWarehouseCart, type CartItem } from "@/state/warehouseCart";
import { saveCartAsDoc, formatRub, formatQty } from "@/api/warehouse";
import { ApiError } from "@/api/client";


interface Props {
  open: boolean;
  onClose: () => void;
}


export function CartSidebar({ open, onClose }: Props) {
  const cart = useWarehouseCart();
  const qc = useQueryClient();
  const [docNumber, setDocNumber] = useState("");
  const [docDate, setDocDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [contractor, setContractor] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);

  const inflows  = cart.items.filter(it => it.quantity > 0);
  const outflows = cart.items.filter(it => it.quantity < 0);

  const saveMut = useMutation({
    mutationFn: async (direction: "inflow" | "outflow") => {
      const items = direction === "inflow" ? inflows : outflows;
      if (!items.length) throw new Error(`Нет позиций для ${direction === "inflow" ? "прихода" : "расхода"}`);
      const warehouse = items[0]?.warehouse;
      return saveCartAsDoc({
        direction,
        warehouse,
        doc_number: docNumber.trim() || null,
        doc_date: docDate,
        contractor_name: contractor.trim() || null,
        note: note.trim() || null,
        items: items.map(it => ({
          item_id: it.item_id,
          quantity: Math.abs(it.quantity),
          price: it.price,
          note: it.note,
        })),
      });
    },
    onSuccess: (_data, direction) => {
      // Удаляем сохранённые позиции из корзины (оставляем только противоположное направление если было)
      if (direction === "inflow") {
        // удаляем все с положительным qty
        for (const it of inflows) cart.remove(it.item_id);
      } else {
        for (const it of outflows) cart.remove(it.item_id);
      }
      void qc.invalidateQueries({ queryKey: ["warehouse-stocks"] });
      void qc.invalidateQueries({ queryKey: ["warehouse-overview"] });
      void qc.invalidateQueries({ queryKey: ["warehouse-inflow-list"] });
      void qc.invalidateQueries({ queryKey: ["warehouse-outflow-list"] });
      setError(null);
    },
    onError: (e) => {
      const msg = e instanceof ApiError
        ? ((e.body as { detail?: string })?.detail || `HTTP ${e.status}`)
        : (e as Error).message;
      setError(msg);
    },
  });

  if (!open) return null;

  return (
    <div
      role="dialog"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 100,
      }}
    >
      <div
        className="card"
        style={{
          position: "absolute", top: 0, right: 0, bottom: 0,
          width: "min(560px, 100%)", maxWidth: "100%",
          padding: 20, overflowY: "auto", display: "flex", flexDirection: "column", gap: 12,
          borderRadius: 0,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
          <div style={{ fontFamily: "var(--serif)", fontSize: 22, fontWeight: 700, color: "var(--ink)" }}>
            Корзина <span style={{ fontSize: 14, color: "var(--mute)" }}>· {cart.count} поз.</span>
          </div>
          <button type="button" onClick={onClose} aria-label="Закрыть"
                  style={{ background: "transparent", border: "none", color: "var(--mute)", cursor: "pointer", fontSize: 22 }}>×</button>
        </div>

        {cart.items.length === 0 ? (
          <div className="muted" style={{ padding: "32px 0", textAlign: "center" }}>
            Корзина пуста.<br />
            Используйте кнопки <strong>+ / −</strong> в таблице остатков.
          </div>
        ) : (
          <>
            <CartGroup
              title={<><Icon name="arrow_down" size={12}/> Приход <span style={{ color: "var(--mute)", fontWeight: 400, fontSize: 12 }}>· {inflows.length}</span></>}
              items={inflows}
              cart={cart}
            />
            <CartGroup
              title={<><Icon name="arrow_up" size={12}/> Расход <span style={{ color: "var(--mute)", fontWeight: 400, fontSize: 12 }}>· {outflows.length}</span></>}
              items={outflows}
              cart={cart}
            />

            <div style={{ borderTop: "1px solid var(--line-soft)", paddingTop: 12, marginTop: 8 }}>
              <div style={{ fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--mute)", marginBottom: 8 }}>
                Реквизиты документа
              </div>
              <div className="row row-2" style={{ marginBottom: 8 }}>
                <label style={fieldStyle}>
                  Дата
                  <input type="date" className="input-sm" value={docDate}
                         onChange={e => setDocDate(e.target.value)} />
                </label>
                <label style={fieldStyle}>
                  № накладной
                  <input type="text" className="input-sm" value={docNumber}
                         onChange={e => setDocNumber(e.target.value)} placeholder="авто" />
                </label>
              </div>
              <label style={{ ...fieldStyle, marginBottom: 8 }}>
                Контрагент
                <input type="text" className="input-sm" value={contractor}
                       onChange={e => setContractor(e.target.value)} placeholder="Поставщик/Заказчик" />
              </label>
              <label style={fieldStyle}>
                Примечание
                <textarea className="input-sm" rows={2} value={note}
                          onChange={e => setNote(e.target.value)}
                          style={{ resize: "vertical", fontFamily: "var(--sans)" }} />
              </label>
            </div>

            {error && <div className="alert alert-red"><div className="alert-body">{error}</div></div>}

            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
              <button
                type="button" className="btn btn-primary"
                disabled={inflows.length === 0 || saveMut.isPending}
                onClick={() => saveMut.mutate("inflow")}
              >
                <Icon name="arrow_down" size={12}/> Сохранить приход ({inflows.length})
              </button>
              <button
                type="button" className="btn"
                disabled={outflows.length === 0 || saveMut.isPending}
                onClick={() => saveMut.mutate("outflow")}
                style={{ background: "var(--brick)", color: "#fff" }}
              >
                <Icon name="arrow_up" size={12}/> Сохранить расход ({outflows.length})
              </button>
              <button type="button" className="btn" onClick={() => { if (window.confirm("Очистить корзину?")) cart.clear(); }}>
                <Icon name="trash" size={12}/> Очистить
              </button>
            </div>

            <div style={{ marginTop: 8, fontSize: 12, color: "var(--mute)" }}>
              Итог: <strong>{formatRub(cart.total)}</strong>
            </div>
          </>
        )}
      </div>
    </div>
  );
}


function CartGroup({ title, items, cart }: {
  title: React.ReactNode;
  items: CartItem[];
  cart: ReturnType<typeof useWarehouseCart>;
}) {
  if (items.length === 0) return null;
  return (
    <div>
      <div style={{ fontSize: 12, color: "var(--ink-3)", fontWeight: 600, marginBottom: 6 }}>{title}</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {items.map(it => {
          const qty = Math.abs(it.quantity);
          return (
            <div key={it.item_id} style={{
              display: "flex", alignItems: "center", gap: 6, padding: "6px 8px",
              background: "var(--paper)", borderRadius: 4, fontSize: 12,
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.name}</div>
                <div style={{ fontSize: 10.5, color: "var(--mute)" }}>{it.sku || "—"} · {it.warehouse}</div>
              </div>
              <button className="btn btn-sm" onClick={() => cart.setQty(it.item_id, it.quantity > 0 ? -1 : 1)}>−</button>
              <span className="tabular" style={{ minWidth: 40, textAlign: "center", fontWeight: 600 }}>
                {formatQty(qty, it.unit)}
              </span>
              <button className="btn btn-sm" onClick={() => cart.setQty(it.item_id, it.quantity > 0 ? 1 : -1)}>+</button>
              <span className="tabular" style={{ minWidth: 80, textAlign: "right", color: "var(--mute)" }}>
                {it.price ? formatRub(qty * it.price) : "—"}
              </span>
              <button className="btn btn-sm" onClick={() => cart.remove(it.item_id)} style={{ color: "var(--brick)" }}>×</button>
            </div>
          );
        })}
      </div>
    </div>
  );
}


const fieldStyle: React.CSSProperties = {
  display: "flex", flexDirection: "column", gap: 4, fontSize: 12, color: "var(--ink-3)",
};
